import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { SectionLabel } from './SectionLabel';
const CASES = [{
  client: 'Porto Seguro',
  project: 'Experiencia de marca con pantallas LED para un encuentro corporativo.',
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/a3a411d5-8991-4ef1-9460-0dc16546ca76.jpg'
}, {
  client: 'Verdirrojo BBC',
  project: 'Cobertura audiovisual y contenidos para acompañar la temporada deportiva.',
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/b00001f8-ac9c-4a65-bdcd-2ec3aa830aea.jpg'
}, {
  client: 'Los Álamos',
  project: 'Fotografía y comunicación visual para experiencias de hospitalidad.',
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/bc96e757-158c-4bc4-9486-651eb819f1ca.jpg'
}, {
  client: 'Luces Pro',
  project: 'Producción técnica, cámaras y visuales para espectáculos en vivo.',
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/e089bcce-961e-4e9f-801b-d1a04a965b2d.jpg'
}, {
  client: 'SoClean',
  project: 'Contenido de producto y fotografía comercial para redes.',
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/f08e3dd2-2795-44b0-a246-52d12e0dcbbc.jpg'
}, {
  client: 'Nacional Fútbol Club',
  project: 'Soluciones visuales y cobertura para una experiencia de transmisión deportiva.',
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/367c1d3b-abd2-4041-b4c2-9424b216f8b1.jpg'
}, {
  client: 'La Farola',
  project: 'Fotografía, gráfica y señalética para una experiencia de marca gastronómica.',
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/cdda8a6a-24cf-41ab-9590-1798b487681d.jpg'
}];
const AUTO_SCROLL_DELAY = 4200;
export function Clients() {
  const carouselRef = useRef<HTMLDivElement>(null);
  const [canGoBack, setCanGoBack] = useState(false);
  const [canGoForward, setCanGoForward] = useState(true);
  const updateControls = () => {
    const carousel = carouselRef.current;
    if (!carousel) return;
    setCanGoBack(carousel.scrollLeft > 4);
    setCanGoForward(carousel.scrollLeft < carousel.scrollWidth - carousel.clientWidth - 4);
  };
  useEffect(() => {
    updateControls();
    window.addEventListener('resize', updateControls);
    return () => window.removeEventListener('resize', updateControls);
  }, []);
  const moveCarousel = (direction: -1 | 1) => {
    const carousel = carouselRef.current;
    if (!carousel) return;
    carousel.scrollBy({
      left: direction * carousel.clientWidth * 0.82,
      behavior: 'smooth'
    });
    window.setTimeout(updateControls, 350);
  };
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const intervalId = window.setInterval(() => {
      const carousel = carouselRef.current;
      if (!carousel) return;
      const isAtEnd = carousel.scrollLeft >= carousel.scrollWidth - carousel.clientWidth - 4;
      if (isAtEnd) {
        carousel.scrollTo({
          left: 0,
          behavior: 'smooth'
        });
      } else {
        carousel.scrollBy({
          left: carousel.clientWidth * 0.82,
          behavior: 'smooth'
        });
      }
      window.setTimeout(updateControls, 350);
    }, AUTO_SCROLL_DELAY);
    return () => window.clearInterval(intervalId);
  }, []);
  return <section id="clientes" className="relative w-full overflow-x-hidden bg-vm-cream py-28 lg:py-36" data-id="element-26" data-mp="u64twz0gsr31">
      <img src="https://cdn.magicpatterns.com/uploads/uDb68aiYVQ97ej61yrYj5i/Isologo_Blanco.png" alt="" aria-hidden="true" className="pointer-events-none absolute inset-y-0 my-auto h-[88%] max-w-none object-contain opacity-55 drop-shadow-[0_12px_12px_rgba(10,14,26,0.22)] right-[-38%] sm:right-[-22%] lg:right-[-4%]" data-id="element-27" data-mp="z4y68o40mc1m" />
      <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-10" data-id="element-28" data-mp="bf3rg104ydeq">
        <div className="flex items-end justify-between gap-6" data-id="element-29" data-mp="gdojhd58hqs3">
          <div className="max-w-4xl" data-id="element-30" data-mp="2i3vs2es5frd8">
            <SectionLabel tone="blue" data-id="element-31" data-mp="2i4xd9c2vpu8o">Casos de éxito</SectionLabel>
            <h2 className="mt-6 font-bornia text-4xl font-bold leading-[1.03] text-vm-bg sm:text-5xl lg:text-[56px]" data-id="element-32" data-mp="2e2k92s3s64e0">
              Clientes que eligieron hacer visible su proyecto
            </h2>
            <p className="mt-6 max-w-2xl text-lg font-light leading-relaxed text-vm-navy" data-id="element-33" data-mp="2n38ptukk6iul">
              Una selección de producciones y soluciones visuales desarrolladas para marcas, instituciones y equipos que buscan conectar con su audiencia.
            </p>
          </div>

          <div className="hidden items-center gap-3 sm:flex" aria-label="Controles del carrusel de casos de éxito" data-id="element-34" data-mp="2wv4nj4iqg6ya">
            <CarouselButton direction="back" disabled={!canGoBack} onClick={() => moveCarousel(-1)} data-id="element-35" data-mp="35tgkrpe3u2zp" />
            <CarouselButton direction="forward" disabled={!canGoForward} onClick={() => moveCarousel(1)} data-id="element-36" data-mp="1gea5i8ocj8ks" />
          </div>
        </div>

        <motion.div ref={carouselRef} initial={{
        opacity: 0,
        y: 22
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-40px'
      }} transition={{
        duration: 0.5
      }} onScroll={updateControls} className="mt-16 flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth pb-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" aria-label="Casos de éxito de Vivo Media" data-id="element-37" data-mp="1bgictcfvnifj">
          {CASES.map(caseStudy => <article key={caseStudy.client} className="w-[86%] shrink-0 snap-start overflow-hidden rounded-2xl border border-vm-bg bg-white sm:w-[48%] lg:w-[32%]" data-id="element-38" data-mp="34aq5c45cttb9">
              <img src={caseStudy.image} alt={`Proyecto realizado para ${caseStudy.client}`} className="aspect-[4/3] w-full object-cover" data-id="element-39" data-mp="2vdfps6li7i0j" />
              <div className="border-t border-vm-bg/20 p-6" data-id="element-40" data-mp="tl19fbylylm8">
                <p className="text-xs font-medium uppercase tracking-[0.25em] text-vm-electric" data-id="element-41" data-mp="xkto86elho01">Caso de éxito</p>
                <h3 className="mt-3 font-bornia text-2xl font-semibold text-vm-bg lg:text-[28px]" data-id="element-42" data-mp="26jl0k54g7jhc">{caseStudy.client}</h3>
                <p className="mt-3 font-light leading-relaxed text-vm-navy" data-id="element-43" data-mp="21iduqblv8udb">{caseStudy.project}</p>
              </div>
            </article>)}
        </motion.div>

        <div className="mt-6 flex items-center justify-between gap-5 sm:hidden" data-id="element-44" data-mp="1sk2449l01wkv">
          <div className="flex items-center gap-3" aria-label="Controles del carrusel de casos de éxito" data-id="element-45" data-mp="2p9j88k4c3akg">
            <CarouselButton direction="back" disabled={!canGoBack} onClick={() => moveCarousel(-1)} data-id="element-46" data-mp="2l8qyzqvgp5o6" />
            <CarouselButton direction="forward" disabled={!canGoForward} onClick={() => moveCarousel(1)} data-id="element-47" data-mp="2gfxun43wnzcm" />
          </div>
        </div>

        <p className="mt-10 border-t border-vm-bg/25 pt-5 text-sm font-light text-vm-navy" data-id="element-48" data-mp="2bfsae8zytf4k">
          Estamos sumando nuevos casos y alianzas a esta selección.
        </p>
      </div>
    </section>;
}
interface CarouselButtonProps {
  direction: 'back' | 'forward';
  disabled: boolean;
  onClick: () => void;
}
function CarouselButton({
  direction,
  disabled,
  onClick
}: CarouselButtonProps) {
  const isBack = direction === 'back';
  const Icon = isBack ? ChevronLeftIcon : ChevronRightIcon;
  return <button type="button" onClick={onClick} disabled={disabled} aria-label={isBack ? 'Ver casos anteriores' : 'Ver más casos'} className="flex h-11 w-11 items-center justify-center rounded-full border border-vm-bg/40 text-vm-bg transition-colors hover:border-vm-bg hover:bg-vm-bg hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-vm-electric disabled:cursor-not-allowed disabled:opacity-35" data-id="element-49" data-mp="3lhz2htx0rror">
      <Icon className="h-5 w-5" aria-hidden="true" data-id="element-50" data-mp="d0cgi33ta21a" />
    </button>;
}